import { callFirebaseFunction, db } from 'boot/firebase';
import { network as globalNetwork } from 'boot/network';
import EditUser from 'components/Users/Modals/EditUser';
import {
  collection, doc, serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { chain, chunk, difference, filter, includes, isEmpty, isNil, map } from 'lodash';
import { mapActions, mapState } from 'pinia';
import TriggerMailsDB from 'src/fb/triggerMails-db';
import UserInvitationsDB from 'src/fb/userInvitations-db';
import UserSettingsDB from 'src/fb/userSettings-db';
import GenericIndexedDb from 'src/indexed-db/generic-indexedDb';
import Interactions from 'src/mixins/Interactions';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';
import { network } from 'src/store/network';
import { region } from 'src/store/region';
import { units } from 'src/store/units';
import { users as usersStore } from 'src/store/users';
import AppUtils, { userRoles } from 'src/utils/AppUtils';
import { logEvent } from 'src/utils/LogEvent';
import { isActiveUser } from 'src/utils/UserUtils';

export default {
  mixins: [Interactions],

  created() {
    this.loadClinicUnits();

    if ((this.isManagerOrAdministrator || this.isSupervisor) && (this.userDetails.clinic)) {
      this.bindUsers(this.userDetails.clinic)
        .then(() => {
          return this.updateClientInvitations();
        })
        .then(() => {
          this.loadingUsers = false;
        });
    }
  },

  data() {
    return {
      loadingUsers: true,
      isWithdrawUserInvitationLoading: false,
      selectedUnits: [],
      editedClientUsersIds: [],

      usersFilter: '',
      clientUsers: [],
      staffUsers: [],

      userColumns: [
        {
          name: 'name',
          required: true,
          label: this.$t('clients.modals.editClient.columnsUserNameLabel'),
          align: 'left',
          field: 'name',
          sortable: true,
        },
        {
          name: 'email',
          required: false,
          label: this.$t('clients.modals.editClient.columnsUserEmailLabel'),
          align: 'left',
          field: 'email',
          sortable: true,
        },
        {
          name: 'role',
          required: false,
          label: this.$t('clients.modals.editClient.columnsUserRoleLabel'),
          align: 'left',
          field: 'role',
          format: (role, user) => chain(this.userPrivilegesList)
            .find({ value: user.userPrivilege || user.role })
            .get('label', this.$t('settings.users.labelUserHasNoRole'))
            .value(),
          sortable: true,
        },
      ],

      usersPagination: {
        rowsPerPage: 50,
        sortBy: 'name',
      },

      unitsColumns: [
        {
          name: 'unitName',
          required: true,
          label: this.$t('settings.units.columnUnitLabel'),
          align: 'left',
          field: 'unitName',
          sortable: true,
        },
      ],

      unitsPagination: {
        rowsPerPage: 50,
        sortBy: 'unitName',
      },

      // invitations
      invitationUser: {
        id: null,
        name: null,
        email: null,
        clinic: null,
        role: null,
      },
      invitation: {
        client: null,
        clientName: null,
        email: null,
        role: null,
      },
      editedInvitationClientIds: [],
      showInviteUser: false,
      savingSpinner: false,
      showInvite: false,
      // invitations: [],
      clientInvitations: [],
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'currentClient', 'isManagerOrAdministrator',
      'isSupervisor', 'userPrivilegesList']),
    ...mapState(region, ['region']),
    ...mapState(units, ['units']),
    ...mapState(usersStore, ['users']),
    ...mapState(network, ['offlineMode']),

    clientUsersSet() {
      return new Set(this.clientUsers);
    },

    selectedUsers: {
      get() {
        return this.users
          .filter((user) => (
            this.clientUsersSet.has(user.userId)
            || (isActiveUser(user)
              && [userRoles.MANAGER, userRoles.ADMINISTRATOR].includes(user.role))
          ));
      },
      set(users) {
        this.clientUsers = map(users, (user) => user.id);
      },
    }, // selectedClients()

    clinicHasUnits() {
      return !isEmpty(this.units);
    }, // clinicHasUnits()

    hasAtLeastOneUnitUsed() {
      return this.clinicHasUnits && !isEmpty(filter(this.unitsWithClients, (unit) => !isEmpty(unit.clients)));
    }, // hasAtLeastOneUnitUsed()

    unitsWithClients() {
      return map(this.units, (unit) => ({
        ...unit,
        clients: includes(this.client.units, unit.id) ? [this.client] : [],
      }));
    }, // unitsWithClients()

    existingAndInvitedUsers() {
      return [...this.users, ...this.clientInvitations];
    },

    availableUsers() {
      const activeUsers = this.existingAndInvitedUsers.filter(isActiveUser);

      return this.isSupervisor
        ? activeUsers.filter((user) => ![userRoles.MANAGER, userRoles.ADMINISTRATOR].includes(user.role))
        : activeUsers;
    },

    infoText() {
      return this.isNewClient
        ? this.$t(
          'clients.modals.manageTrainers.textDescription',
          {
            clientName: `<span class="text-weight-bold">${this.editClient.clientName
            || this.$t('clients.modals.editClient.newClientName')}</span>`,
          },
        )
        : this.$t(
          'clients.modals.manageTrainers.textDescription',
          { clientName: `<span class="text-weight-bold">${this.client.clientName}</span>` },
        );
    }, // infoText()

    isNewClient() {
      return isNil(this.client.id);
    }, // isNewClient()
  },

  methods: {
    ...mapActions(usersStore, ['bindUsers']),
    ...mapActions(units, ['loadClinicUnits']),
    ...mapActions(clients, ['deleteClient']),

    async onInviteUser(userRole) {
      const invitationUser = {
        id: null,
        name: '',
        email: '',
        clinic: this.userDetails.clinic,
        role: userRole.role || null,
        userPrivilege: userRole.isCustomRole ? userRole.value : null,
      };
      const associatedClients = this.client.id ? [this.client.id] : [];

      this.$q.loading.show();

      this.openComponentDialog(
        EditUser,
        {
          user: invitationUser,
          associatedClients,
          title: this.$t('users.modals.editUser.textTitleInvite'),
          allUsers: this.users,
          hideName: true,
          saveButton: this.$t('users.modals.editUser.sendInvitationButton'),
          invitePageInput: true,
        },
        async ({ user, invitePage, staffMemberInfo }) => {
          this.savingSpinner = true;

          try {
            const id = await this.createNewStaffMember(staffMemberInfo);
            user.staffMember = id || null;
            staffMemberInfo.editStaffMember.id = id;

            const userClientAssociations = this.getUserClientAssociations(user, staffMemberInfo);
            const userClientIds = map(userClientAssociations, 'client');

            await this.sendInvitation(user, userClientIds, invitePage);
          } catch (err) {
            this.notifyNegative(err.message);
          } finally {
            this.savingSpinner = false;
            this.updateClientInvitations();
          }
        },
      );
      this.$q.loading.hide();
    },

    async onWithdrawUserInvitation(invitation) {
      this.confirmDialog(
        this.$t('common.dialogTitleConfirm'),
        this.$t('clients.modals.editClient.dialogWithdrawMessage', { user: invitation.email }),
        async () => {
          try {
            this.isWithdrawUserInvitationLoading = true;
            if (invitation.staffMember) {
              await this.deleteClient(invitation.staffMember);
              await callFirebaseFunction('updateSubscriptionUsage', {
                clinicId: invitation.clinic,
                adjustLicenses: true,
              });
            }

            const userInvitationsDb = new UserInvitationsDB();
            await userInvitationsDb.deleteSubcollection(invitation.email, 'invitationClients');
            await userInvitationsDb.delete(invitation.email);
            this.notifySuccess(this.$t('clients.modals.manageTrainers.notifyInvitationWithdrawSuccess'));
            this.updateClientInvitations();
          } catch (err) {
            logEvent(err);
            this.notifyNegative(this.$t(
              'clients.modals.manageTrainers.notifyInvitationWithdrawError',
              { error: err.message },
            ));
          } finally {
            this.isWithdrawUserInvitationLoading = false;
          }
        },
        { cancel: true, persistent: true },
      );
    },

    isInvitedUser(props) {
      return props.row?.status === 'invited';
    },

    /**
     * Handles {@link EditUser.vue} $emit 'save'
     * @param {Object} inviteUser - Obj with info about user we want to invite
     * @param {Array<Object>} userClientIds - Array of clients we want to bind to new user
     * @param {String} invitePage - Name of route user should use to register
     */
    async sendInvitation(inviteUser, userClientIds, invitePage = 'signup') {
      this.savingSpinner = true;
      const userInvitation = {
        role: inviteUser.role,
        userPrivilege: inviteUser.userPrivilege || null,
        invitedByUser: this.userDetails.userId,
        name: inviteUser.name,
        email: inviteUser.email.toLowerCase().trim(),
        clinic: inviteUser.clinic,
        requireMFA: inviteUser.requireMFA || false,
        requireLoginMethod: inviteUser.requireLoginMethod || false,
        loginMethod: inviteUser.loginMethod || null,
        units: inviteUser.units || [],
        staffMember: inviteUser.staffMember || null,
      };

      const userInvitationsDb = new UserInvitationsDB();

      try {
        await userInvitationsDb.create(userInvitation, userInvitation.email, true);
      } catch (err) {
        this.notifyNegative(this.$t(
          'settings.users.notifySendInvitationFailedAlreadyInvited',
          { email: userInvitation.email },
        ));
        return;
      }

      if (!isEmpty(userClientIds)) {
        try {
          await userInvitationsDb.addClients(userInvitation.email, userClientIds);
        } catch (err) {
          this.notifyNegative(this.$t('settings.users.notifySendInvitationFailedFailed', { message: err.message }));
        }
      }

      await this.sendInvitationMail(userInvitation, invitePage);
    }, // sendInvitation()

    /**
     * Sending invitation mail to new user's email
     * @param {Object} userInvitation - userInvitation object
     * @param {String: 'signup'} invitePage - Name of route user should use to register
     *
     * @private
     */
    async sendInvitationMail(userInvitation, invitePage) {
      const signupQueryParams = new URLSearchParams({
        email: userInvitation.email,
        region: this.region,
        ...(userInvitation.name && { name: userInvitation.name }),
        ...(userInvitation.requireLoginMethod && userInvitation.loginMethod
          ? { loginMethod: userInvitation.loginMethod }
          : {}),
      });

      const templateData = {
        sender_name: this.userDetails.name,
        recipient_email: userInvitation.email,
        signup_link: `${AppUtils.getCurrentOrigin()}/#/${invitePage}?${signupQueryParams.toString()}`,
      };

      const triggerMailsDb = new TriggerMailsDB();
      await triggerMailsDb.sendMail([userInvitation.email], 'invitation', templateData);

      this.notifySuccess(this.$t('settings.users.notifySendInvitationSuccess', { email: userInvitation.email }));
      this.showInvite = false;
    },

    /**
     * Get invitations, associated with client we are working with
     */
    async updateClientInvitations() {
      if (this.isNewClient || this.offlineMode) {
        return;
      }

      this.loadingUsers = true;

      try {
        const userInvitationsDb = new UserInvitationsDB();
        const result = await userInvitationsDb.readByClient(this.client.id);
        this.clientInvitations = result.map((inv) => ({
          // name: 'zzz' - required hack for right sorting by name
          id: inv.id,
          clinic: inv.clinic,
          email: inv.email,
          role: inv.role,
          userPrivilege: inv.userPrivilege || null,
          status: 'invited',
          staffMember: inv.staffMember,
          units: inv.units,
          name: 'zzz',
        }));
      } catch (err) {
        console.error('Error reading client invitations', err);
        logEvent(err);
      } finally {
        this.loadingUsers = false;
      }
    }, // updateClientInvitations()

    async updateClientUsersBinding(client) {
      const addClientUsers = difference(this.clientUsers, this.associatedUsers);
      const deleteClientUsers = difference(this.associatedUsers, this.clientUsers);

      const MAX_BATCH_SIZE = 500;
      const collectionRef = collection(db, 'usersClients');
      const timestamp = serverTimestamp();
      const usersLookup = new Map(this.users.map((user) => [user.userId, user]));

      // batch create user-client bindings
      const addChunks = chunk(addClientUsers, MAX_BATCH_SIZE);
      const addBatchPromises = addChunks.map((addChunk) => {
        const batch = writeBatch(db);
        addChunk.forEach((clientUserId) => {
          const user = usersLookup.get(clientUserId);
          if (!user) return;
          const docRef = doc(collectionRef, `${clientUserId}_${client.id}`);
          batch.set(docRef, {
            client: client.id,
            user: clientUserId,
            userName: user.name,
            role: user.role,
            userPrivilege: user.userPrivilege || null,
            invitedByUser: this.userDetails.userId,
            createTimestamp: timestamp,
            updateTimestamp: timestamp,
          });
        });
        return batch.commit();
      });

      // batch delete user-client bindings
      const deleteChunks = chunk(deleteClientUsers, MAX_BATCH_SIZE);
      const deleteBatchPromises = deleteChunks.map((deleteChunk) => {
        const batch = writeBatch(db);
        deleteChunk.forEach((clientUserId) => {
          const docRef = doc(collectionRef, `${clientUserId}_${client.id}`);
          batch.delete(docRef);
        });
        return batch.commit();
      });

      await Promise.all([...addBatchPromises, ...deleteBatchPromises]);

      // update IndexedDB if offline mode is available
      if (globalNetwork.offlineMode || globalNetwork.offlineModeAvailable) {
        const idxDb = new GenericIndexedDb('usersClients');
        const idxDbPromises = [
          ...addClientUsers.map((clientUserId) => {
            const user = usersLookup.get(clientUserId);
            if (!user) return Promise.resolve();
            return idxDb.create({
              client: client.id,
              user: clientUserId,
              userName: user.name,
              role: user.role,
              userPrivilege: user.userPrivilege || null,
              invitedByUser: this.userDetails.userId,
            }, `${clientUserId}_${client.id}`);
          }),
          ...deleteClientUsers.map((clientUserId) => idxDb.delete(`${clientUserId}_${client.id}`)),
        ];
        await Promise.all(idxDbPromises);
      }

      // update offline clients settings for removed users
      const updateUserOfflineClientsActions = deleteClientUsers.map(async (userId) => {
        const userSettingsDb = new UserSettingsDB(userId);
        const userSettings = await userSettingsDb.read('settings');
        const offlineClientsIds = userSettings?.offlineClients;

        if (!offlineClientsIds || !offlineClientsIds?.length) {
          return;
        }

        const updatedOfflineClients = offlineClientsIds.filter((clientId) => clientId !== client.id);

        if (updatedOfflineClients.length === offlineClientsIds.length) {
          return;
        }

        await userSettingsDb.set({ offlineClients: updatedOfflineClients }, 'settings');
      });

      await Promise.all(updateUserOfflineClientsActions);
    }, // updateClientUsersBinding()

    isUserManagerOrAdministrator(user) {
      return [userRoles.MANAGER, userRoles.ADMINISTRATOR].includes(user.role);
    }, // isUserManagerOrAdministrator()

  },
};
